import type { DmConversation, DmMessage, DmMessagesPage } from '@/types/dm';

/** Il limite di QSocials (`MESSAGE_MAX_CHARS`), contato come lui: `text.trim().length`, unita' UTF-16. */
export const DM_MAX_CARATTERI = 2000;
/** Da qui in su il contatore compare, prima di arrivare al limite. */
export const DM_AVVISO_CARATTERI = 1800;

export const lunghezzaDm = (testo: string): number => testo.trim().length;
export const inviabileDm = (testo: string): boolean => {
  const n = lunghezzaDm(testo);
  return n > 0 && n <= DM_MAX_CARATTERI;
};

/** Pagine (dalla piu' recente) → messaggi dal piu' VECCHIO al piu' nuovo, uno per `seq`. */
export function messaggiInOrdine(pagine: readonly DmMessagesPage[] | undefined): DmMessage[] {
  const perSeq = new Map<number, DmMessage>();
  for (const p of pagine ?? []) for (const m of p.messages) if (!perSeq.has(m.seq)) perSeq.set(m.seq, m);
  return [...perSeq.values()].sort((a, b) => a.seq - b.seq);
}

/**
 * Un messaggio arrivato dal canale `dm:<id>` (o la risposta dell'invio) entra nella pagina piu' recente,
 * senza una GET. Gia' presente (stesso `seq`) ⇒ non cambia niente: il tempo reale e la risposta
 * dell'invio portano lo stesso messaggio due volte.
 */
export function inserisciMessaggio(pagine: DmMessagesPage[], m: DmMessage): void {
  if (pagine.some((p) => p.messages.some((x) => x.seq === m.seq))) return;
  if (pagine.length === 0) pagine.push({ messages: [], nextCursor: null });
  pagine[0].messages.unshift(m);
}

/** I rifiuti del dominio (D2, D3) come li dice lo schermo. */
export type RifiutoDm = 'chiusa' | 'troppi' | 'lungo' | 'altro';
export function rifiutoDm(errore: unknown): RifiutoDm {
  const e = errore as { status?: unknown; data?: { code?: unknown } } | undefined;
  const code = e?.data?.code;
  if (e?.status === 403) return 'chiusa';
  if (e?.status === 429) return 'troppi';
  if (e?.status === 400 && code === 'invalid_text') return 'lungo';
  return 'altro';
}

/**
 * Le conversazioni da mostrare nell'elenco: si tolgono quelle APERTE e mai scritte (chi tocca «Scrivi» e
 * torna indietro lascerebbe una riga vuota); le chiuse restano, senza anteprima (§1-ter).
 */
export function conversazioniDaMostrare(elenco: readonly DmConversation[] | undefined): DmConversation[] {
  return (elenco ?? []).filter((c) => c.closed === true || c.lastMessage !== null);
}

/** Un evento del canale `dm:<conversationId>` (§1-bis, canale 2). Sconosciuto o malformato ⇒ `null`. */
export type EventoConversazione = { tipo: 'messaggio'; messaggio: DmMessage } | { tipo: 'cancellati' };
export function eventoConversazione(data: unknown): EventoConversazione | null {
  if (data === null || typeof data !== 'object') return null;
  const e = data as { type?: unknown; message?: Partial<DmMessage> };
  if (e.type === 'message') {
    const m = e.message;
    if (!m || typeof m.seq !== 'number' || typeof m.text !== 'string' || typeof m.senderId !== 'string') return null;
    return { tipo: 'messaggio', messaggio: m as DmMessage };
  }
  // `erased`: `senderId` ha cancellato i suoi messaggi (P1) ⇒ la conversazione aperta si rilegge.
  if (e.type === 'erased') return { tipo: 'cancellati' };
  return null;
}

/**
 * Il giorno dell'ultimo messaggio rispetto ad `adesso`, nel fuso del telefono: la riga dell'elenco mostra
 * l'ora (oggi), «Ieri», o la data. Per giorno di CALENDARIO, non per 24 ore: le 23:50 di ieri viste alle
 * 00:10 sono «Ieri», non l'ora. Data illeggibile ⇒ `null` (la riga non mostra niente).
 */
export type GiornoRiga = 'oggi' | 'ieri' | 'prima';
export function giornoRiga(ts: string, adesso: Date): GiornoRiga | null {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const inizio = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const oggi = inizio(adesso);
  const giorno = inizio(d);
  if (giorno >= oggi) return 'oggi';
  const ieri = inizio(new Date(adesso.getFullYear(), adesso.getMonth(), adesso.getDate() - 1));
  return giorno >= ieri ? 'ieri' : 'prima';
}

/** La conversazione e' una sottopagina di Amici (la `TabBar` resta su «friends», ma non si vede). */
export const DM_PATH_CONVERSAZIONE = 'friends/messages/:conversationId';
export const percorsoConversazione = (id: string): string => `/friends/messages/${encodeURIComponent(id)}`;
